import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useAuth } from "../auth/AuthContext";
import Pagination from "../components/Pagination";
import { formatCurrency } from "../utils/currency";
import { localIsoDate } from "../utils/date";

const PAGE_SIZE = 20;

const EMPTY_FORM = { category_id: "", amount: "", description: "", expense_date: "", recurrence_type: "one_off" };

const RECURRENCE_LABELS = { one_off: "One-off", monthly: "Monthly", annual: "Annual" };
const RECURRENCE_PERIOD_DAYS = { one_off: null, monthly: 30, annual: 365 };

export default function ExpensesPage() {
  const { activeShop } = useAuth();
  const canManage = activeShop?.role === "org_admin" || activeShop?.role === "manager";
  const shopId = activeShop?.shop_id;

  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM, expense_date: localIsoDate() });
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalExpenseCount, setTotalExpenseCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(""), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const fetchCategories = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await api.get(`/shops/${shopId}/expense-categories`);
      setCategories(res.data);
    } catch (err) {
      setError("Failed to load expense categories");
    }
  }, [shopId]);

  const fetchExpenses = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const res = await api.get(`/shops/${shopId}/expenses`, { params: { page, page_size: PAGE_SIZE } });
      setExpenses(res.data.items);
      setTotalExpenseCount(res.data.total);
      setTotalAmount(res.data.total_amount);
    } catch (err) {
      setError("Failed to load expenses");
    }
    setLoading(false);
  }, [shopId, page]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const categoriesById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/shops/${shopId}/expense-categories`, { name: newCategoryName });
      setNewCategoryName("");
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add category");
    }
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, expense_date: localIsoDate() });
    setEditId(null);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = {
        category_id: Number(form.category_id),
        amount: Number(form.amount),
        description: form.description || null,
        expense_date: form.expense_date,
        recurrence_type: form.recurrence_type,
      };
      if (editId) {
        await api.put(`/shops/${shopId}/expenses/${editId}`, payload);
        setMessage("Expense updated successfully");
        resetForm();
        fetchExpenses();
      } else {
        await api.post(`/shops/${shopId}/expenses`, payload);
        setMessage("Expense recorded successfully");
        resetForm();
        if (page === 1) {
          fetchExpenses();
        } else {
          setPage(1);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save expense");
    }
  };

  const handleEdit = (expense) => {
    setForm({
      category_id: expense.category_id,
      amount: expense.amount,
      description: expense.description || "",
      expense_date: expense.expense_date,
      recurrence_type: expense.recurrence_type,
    });
    setEditId(expense.id);
    setMessage("");
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/shops/${shopId}/expenses/${id}`);
      setMessage("Expense deleted successfully");
      fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete expense");
    }
  };

  const currency = (n) => formatCurrency(n, activeShop?.currency);

  const dailyEquivalentDays = RECURRENCE_PERIOD_DAYS[form.recurrence_type];
  const dailyEquivalentHint =
    dailyEquivalentDays && form.amount && Number(form.amount) > 0
      ? `≈ ${currency(Number(form.amount) / dailyEquivalentDays)}/day`
      : null;

  if (!activeShop) {
    return <div className="card">No shop assigned to your account yet.</div>;
  }

  return (
    <>
      <div className="stats">
        <div className="chip">Total: {currency(totalAmount)}</div>
      </div>

      <div className="content-grid">
        {canManage && (
          <div className="card form-card">
            <h2>{editId ? "Edit Expense" : "Record Expense"}</h2>

            <form onSubmit={handleAddCategory} className="stock-form" style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="New category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                required
              />
              <button className="btn btn-secondary" type="submit">
                Add Category
              </button>
            </form>

            <form onSubmit={handleSubmit} className="product-form">
              <select name="category_id" value={form.category_id} onChange={handleChange} required>
                <option value="">Select a category...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <input
                type="number"
                name="amount"
                placeholder="Amount"
                value={form.amount}
                onChange={handleChange}
                required
                step="0.01"
                min="0.01"
              />
              <input
                type="date"
                name="expense_date"
                value={form.expense_date}
                onChange={handleChange}
                required
              />
              <select name="recurrence_type" value={form.recurrence_type} onChange={handleChange}>
                {Object.entries(RECURRENCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="description"
                placeholder="Description (optional)"
                value={form.description}
                onChange={handleChange}
              />
              {dailyEquivalentHint && (
                <div className="field-hint" style={{ gridColumn: "1 / -1" }}>
                  {dailyEquivalentHint} - recognized daily in reports from{" "}
                  {form.expense_date || "the entry date"} onward, indefinitely, until this expense is deleted.
                </div>
              )}
              <div className="form-actions">
                <button className="btn" type="submit">
                  {editId ? "Update" : "Record"}
                </button>
                {editId && (
                  <button className="btn btn-secondary" type="button" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
            {message && <div className="success-msg">{message}</div>}
            {error && <div className="error-msg">{error}</div>}
          </div>
        )}

        <div className="card list-card">
          <h2>Expenses</h2>
          {!canManage && (message || error) && (
            <>
              {message && <div className="success-msg">{message}</div>}
              {error && <div className="error-msg">{error}</div>}
            </>
          )}
          {loading ? (
            <div className="loader">Loading...</div>
          ) : (
            <>
            <div className="scroll-x">
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Recurrence</th>
                    <th>Amount</th>
                    {canManage && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e.id}>
                      <td>{e.expense_date}</td>
                      <td>{categoriesById[e.category_id]?.name || e.category_id}</td>
                      <td className="desc-cell" title={e.description}>
                        {e.description || "-"}
                      </td>
                      <td>
                        {e.recurrence_type === "one_off" ? (
                          <span className="muted">One-off</span>
                        ) : (
                          <span className="chip">{RECURRENCE_LABELS[e.recurrence_type]}</span>
                        )}
                      </td>
                      <td className="price-cell">{currency(e.amount)}</td>
                      {canManage && (
                        <td>
                          <div className="row-actions">
                            <button className="btn btn-edit" onClick={() => handleEdit(e)}>
                              Edit
                            </button>
                            <button className="btn btn-delete" onClick={() => handleDelete(e.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {expenses.length === 0 && (
                    <tr>
                      <td colSpan={canManage ? 6 : 5} className="empty">
                        No expenses recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={Math.max(1, Math.ceil(totalExpenseCount / PAGE_SIZE))}
              onPageChange={setPage}
            />
            </>
          )}
        </div>
      </div>
    </>
  );
}
